import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from '../data/organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';
import { insertActivity } from './activity';
import { buildUpdateDetails } from '../utils/buildActivityDetails';

const getCalendars = async () => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('Calendars').select('*, Roles(*)').eq('organizationID', orgID).eq('calendarType', 'EVENTS').eq('isDeleted', false).order('name');

    if(error){
        ErrorHandler(Error(error.message), {query: "getCalendars", input: {organizationID: orgID}}, `Something went wrong fetching the calendars`)
        return [];
    }

    return data;
}

const getCalendar = async (id) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Calendars').select('*, Roles(*)').eq('organizationID', orgID).eq('isDeleted', false).eq('id', id).maybeSingle();

    if(error){
        ErrorHandler(Error(error?.message), {query: "getCalendar", input: {organizationID: orgID, error}}, `Something went wrong fetching calendar`)
        throw Error()
    }

    return data;
}


const saveCalendar = async (details) => {
    const orgID = await getCurrentOrgID();
    const originalValues = details.originalValues || null;

    let cal = {
        id: details.id,
        name: details.name,
        description: details.description,
        color: details.color,
        organizationID: orgID,
        createdByID: details.createdByID
    }

    if(cal.id){
        return updateCalendar(cal, details.Roles, originalValues);
    }
    else{
        return createCalendar(cal, details.Roles);
    }
}


const createCalendar = async (cal, roles = []) => {
    const {insertError} = await supabase.from('Calendars').insert(cal);
    const {error, data} = await supabase.from('Calendars').select('*, Roles(*)').limit(1).order('createdAt', {ascending: false}).maybeSingle();

    if(insertError || error){
        ErrorHandler(Error(error.message || insertError.message), {mutation: "createCalendar", input: cal, error, insertError}, `Something went wrong creating that calendar`);
        throw Error();
    }

    const {error: roleError, data: roleData} = await supabase.from('CalendarRoles').insert(roles.map(r => ({organizationID: cal.organizationID, calendarID: data.id, roleID: r.id})).flat()).select();

    if(roleError){
        ErrorHandler(Error(roleError.message), {mutation: "createCalendar", input: roles}, `We created the Calendar, but something went wrong creating the roles for it`);
        throw Error();
    }

    // Log activity (fire and forget)
    insertActivity({
        subjectType: 'Calendar',
        subjectID: data.id,
        subjectName: data.name,
        verb: 'create',
    }).catch(() => {});

    return {...data, Roles: roleData};
}


const updateCalendar = async (cal, roles = [], originalValues = null) => {
    const {error, data} = await supabase.from('Calendars').update(cal).eq('id', cal.id).select().single();

    if(error){
        ErrorHandler(error, {mutation: "updateCalendar", input: cal}, `Something went wrong updating that calendar`);
        throw Error();
    }

    // TODO: Change this to a transaction in a function OR compare & update like before
    const {error: deleteError} = await supabase.from('CalendarRoles').delete().eq('calendarID', cal.id).select();
    const {error: insertError, data: insertData} = await supabase.from('CalendarRoles').insert(roles.map(r => ({organizationID: cal.organizationID, calendarID: cal.id, roleID: r.id})).flat()).select();

    if(deleteError || insertError){
        ErrorHandler(Error(deleteError?.message || insertError?.message), {mutation: "updateCalendar", input: roles}, `We updated the Calendar itself, but something went wrong updating the roles for it`);
        throw Error();
    }

    // Log activity (fire and forget)
    const details = originalValues ? buildUpdateDetails(originalValues, cal, {
        name: { label: 'Name' },
        description: { label: 'Description' },
        color: { label: 'Color' },
    }) : null;
    insertActivity({
        subjectType: 'Calendar',
        subjectID: cal.id,
        subjectName: data.name,
        verb: 'update',
        details,
    }).catch(() => {});

    return {...data, Roles: insertData};
}


const deleteCalendar = async (calendar) => {
    //Only mark the calendar as deleted, don't actually delete it
    const input = {
        id: calendar.id,
        name: calendar.name + ' (Deleted)',
        isDeleted: true
    }

    const {error} = await supabase.from('Calendars').update(input).eq('id', calendar.id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteCalendar", input: input}, `Something went wrong deleteing that calendar`)
        throw Error();
    }

    Toast.delayShow({type: 'success', text2: `Calendar has been deleted successfully!`});

    // Log activity (fire and forget) - use the original name, not the ' (Deleted)' suffixed one
    insertActivity({
        subjectType: 'Calendar',
        subjectID: calendar.id,
        subjectName: calendar.name,
        verb: 'delete',
    }).catch(() => {});
}


export { 
    getCalendars,
    getCalendar,
    saveCalendar,
    deleteCalendar
}